import { AlertTriangle, CreditCard, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useCredits } from '@/features/dashboard/hooks/useCredits';
import { cn } from '@/lib/utils';

interface CreditWarningProps {
  threshold?: number;
}

export function CreditWarning({ threshold = 10 }: CreditWarningProps) {
  const { data: credits, isLoading } = useCredits();
  const navigate = useNavigate();

  if (isLoading) return null;

  const balance = credits ?? 0;

  if (balance > threshold) return null;

  const isZero = balance === 0;
  const progressValue = Math.min((balance / threshold) * 100, 100);

  return (
    <Card
      className={cn(
        'border-l-4',
        isZero ? 'border-l-rose-500 bg-rose-50/50' : 'border-l-amber-500 bg-amber-50/50'
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'mt-0.5 rounded-full p-1.5',
              isZero ? 'bg-rose-100 text-rose-600' : 'bg-amber-100 text-amber-600'
            )}
          >
            {isZero ? (
              <AlertTriangle className="h-4 w-4" />
            ) : (
              <CreditCard className="h-4 w-4" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <p
              className={cn(
                'text-sm font-semibold',
                isZero ? 'text-rose-800' : 'text-amber-800'
              )}
            >
              {isZero ? 'No credits remaining' : 'Credits running low'}
            </p>
            <p
              className={cn(
                'mt-0.5 text-sm',
                isZero ? 'text-rose-700' : 'text-amber-700'
              )}
            >
              {isZero
                ? 'You need credits to run evaluations and use AI features.'
                : `You have ${balance} credit${balance !== 1 ? 's' : ''} left. Top up to keep evaluating.`}
            </p>

            <div className="mt-2">
              <Progress
                value={progressValue}
                className={cn(
                  'h-1.5',
                  isZero ? '[&>div]:bg-rose-500' : '[&>div]:bg-amber-500'
                )}
              />
            </div>

            <Button
              size="sm"
              onClick={() => navigate('/pricing')}
              className={cn(
                'mt-3',
                isZero
                  ? 'bg-rose-600 hover:bg-rose-700'
                  : 'bg-amber-600 hover:bg-amber-700'
              )}
            >
              Buy Credits
              <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
